import { useEffect, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';

interface Amenity {
  title?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Marquee animation styles (injected once)
// ============================================================================

const STYLE_ID = 'stadium-scrolling-text-marquee';

const MARQUEE_CSS = `
@keyframes stadium-marquee-scroll {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}

.stadium-marquee {
  display: flex;
  width: max-content;
  animation: stadium-marquee-scroll 200s linear infinite;
  will-change: transform;
}

.stadium-marquee-viewport:hover .stadium-marquee {
  animation-play-state: paused;
}

@media (prefers-reduced-motion: reduce) {
  .stadium-marquee {
    animation: none;
    transform: translateX(0);
  }
}
`;

function useMarqueeStyles(): void {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = MARQUEE_CSS;
    document.head.appendChild(style);
  }, []);
}

const ITEM_CLASS =
  'font-heading font-normal uppercase tracking-[0.15em] text-primary-foreground whitespace-nowrap';
// A CSS-drawn dot gives us a perfect circle at a controlled size, unlike the
// previous Unicode diamond (◆) which rendered as a font glyph and inherited
// the text's asymmetry.
const DIVIDER_CLASS =
  'mx-8 md:mx-12 flex-shrink-0 inline-block rounded-full bg-[var(--vibe-color-text-on-primary)] opacity-100 w-[3px] h-[3px] md:w-[5px] md:h-[5px]';

// Shown only when a property has no amenities configured yet, so the band is
// never empty. Not editable content — just a graceful placeholder.
const FALLBACK_PHRASES = [
  'Now Leasing',
  'Modern Living',
  'Resort-Style Amenities',
  'Your New Home Awaits',
];

const MIN_LOOP_ITEMS = 8;

function isFeatured(value: string | null | undefined): boolean {
  if (!value) return false;
  return ['true', '1', 'yes', 'y'].includes(String(value).trim().toLowerCase());
}

// ============================================================================
// Component
// ============================================================================

export function ScrollingText() {
  useMarqueeStyles();

  const amenities = useCollectionBinding<Amenity>('scrollingtext.amenities', {
    collection: 'amenities',
  });

  // Feature the "best" amenities: prefer the ones flagged featured, then fall
  // back to the rest of the list, then to generic phrases if there are none.
  const phrases = useMemo(() => {
    const titled = amenities
      .map(a => (a.title || '').trim())
      .filter((t): t is string => t.length > 0);

    const featured = amenities
      .filter(a => isFeatured(a.is_featured))
      .map(a => (a.title || '').trim())
      .filter((t): t is string => t.length > 0);

    const base = featured.length > 0 ? featured : titled;
    return base.length > 0 ? base : FALLBACK_PHRASES;
  }, [amenities]);

  // Repeat the base list until it comfortably exceeds the viewport width so the
  // -50% keyframe (two copies) produces a gap-free, continuous loop.
  const loopItems = useMemo(() => {
    if (phrases.length === 0) return [];
    const items: string[] = [];
    while (items.length < MIN_LOOP_ITEMS) items.push(...phrases);
    return items;
  }, [phrases]);

  if (loopItems.length === 0) return null;

  const renderSequence = (keyPrefix: string) =>
    loopItems.map((phrase, i) => (
      <span key={`${keyPrefix}-${i}`} className="flex items-center flex-shrink-0">
        <span className={ITEM_CLASS}>{phrase}</span>
        <span aria-hidden="true" className={DIVIDER_CLASS} />
      </span>
    ));

  return (
    <section
      aria-label="Featured amenities"
      className="w-full overflow-hidden bg-[var(--vibe-color-primary)] border-b border-[var(--vibe-color-text-on-primary)]/15 py-5 md:py-6"
    >
      <div className="stadium-marquee-viewport relative w-full overflow-hidden">
        <div className="stadium-marquee text-lg md:text-2xl">
          {/* First copy — the visible content. */}
          {renderSequence('a')}
          {/* Second copy — aria-hidden duplicate for the seamless -50% loop. */}
          <span aria-hidden="true" className="flex items-center flex-shrink-0">
            {renderSequence('b')}
          </span>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'ScrollingText',
  displayName: 'Scrolling Text',
  tokens: {},
  collectionBindings: {
    'scrollingtext.amenities': {
      label: 'Featured Amenities',
      collection: 'amenities',
    },
  },
};

(ScrollingText as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScrollingText;
